import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/** Route gate. Enforces three layers, in order:
 *
 *  1. Auth — no token? bounce to /login.
 *  2. Forced password change — `must_change_password=true`? bounce to
 *     /change-password (only that route is allowed until cleared).
 *  3. Approval — non-admin with `status != "active"`? bounce to /pending
 *     (only /pending and /profile are allowed). Admins always pass.
 */
const ALLOW_WHILE_CHANGING_PW = new Set(["/change-password"]);
const ALLOW_WHILE_PENDING = new Set(["/pending", "/profile"]);

export default function ProtectedRoute({ children }) {
    const { user } = useAuth();
    const loc = useLocation();
    if (!user) return <Navigate to="/login" replace />;

    // Layer 2 — forced password change.
    if (user.must_change_password && !ALLOW_WHILE_CHANGING_PW.has(loc.pathname)) {
        return <Navigate to="/change-password" replace />;
    }

    // Layer 3 — approval gate (admins bypass).
    const isActive = user.is_admin || user.status === "active" || !user.status;
    if (!isActive && !ALLOW_WHILE_PENDING.has(loc.pathname)) {
        return <Navigate to="/pending" replace />;
    }

    // Inverse: if user is already active and somehow lands on /pending,
    // bounce back to dashboard.
    if (isActive && loc.pathname === "/pending") {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
}
